import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { formatMoney } from "@otrack/domain";
import { Button, Icon, OTrackMark, Segmented, type IconName } from "@otrack/ui";
import type { AssistantStep } from "./assistant";
import { SourceCards } from "./assistant-cards";
import { colorOf } from "./calendar/model";
import type { Answer, AnswerChoice, AnswerRow, AnswerView, EarningsPeriod } from "./commands";
import { useDataset } from "./context";
import { formatClock, formatDuration, formatPeriod, HOUR } from "./time";

type DayView = Extract<AnswerView, { kind: "day" }>;
type EarningsView = Extract<AnswerView, { kind: "earnings" }>;

/** Figures inside an assistant reply: money, durations, and clock times. */
const FIGURE = /([$€£]\s?-?\d[\d,.]*|\b[A-Z]{3}\s-?\d[\d,.]*|\b\d+h(?: \d+m)?\b|\b\d+m\b|\b\d{1,2}:\d{2}\b)/g;
const BULLET = /^\s*(?:[-•*]|\d+[.)])\s+/;

function withFigures(text: string): ReactNode {
  return text.split(FIGURE).map((part, index) => index % 2 ? <b key={index} className="ot-mono">{part}</b> : part);
}

/** Plain text from the assistant as paragraphs and lists, with its figures set apart. */
function Reply({ text }: { text: string }) {
  const blocks: Array<{ list: boolean; lines: string[] }> = [];
  for (const line of text.split("\n")) {
    if (!line.trim()) { blocks.push({ list: false, lines: [] }); continue; }
    const list = BULLET.test(line);
    const last = blocks.at(-1);
    if (last && last.list === list && (list || last.lines.length)) last.lines.push(list ? line.replace(BULLET, "") : line);
    else blocks.push({ list, lines: [list ? line.replace(BULLET, "") : line] });
  }
  return <div className="ot-reply">
    {blocks.filter((block) => block.lines.length).map((block, index) => block.list
      ? <ul key={index}>{block.lines.map((line, item) => <li key={item}>{withFigures(line)}</li>)}</ul>
      : <p key={index} className={index === 0 ? "ot-reply__lead" : undefined}>{block.lines.map((line, item) => <Fragment key={item}>{item > 0 && <br/>}{withFigures(line)}</Fragment>)}</p>)}
  </div>;
}

/** An icon for a change line, read from the verb it starts with. */
function changeIcon(change: string): IconName {
  const verb = change.split(" ")[0]?.toLowerCase();
  if (verb === "plan" || verb === "planned") return "calendar-plus";
  if (verb === "log" || verb === "logged") return "clock";
  if (verb === "start" || verb === "started") return "play";
  if (verb === "stop" || verb === "stopped") return "stop";
  if (verb === "move") return "trash";
  if (verb === "draft") return "receipt";
  if (verb === "mark") return / as paid /.test(change) ? "hand-coins" : "send";
  return "pencil";
}

function Changes({ changes, done = false }: { changes: string[]; done?: boolean }) {
  return <ul className={`ot-changes ${done ? "is-done" : ""}`}>
    {changes.map((change, index) => <li key={`${index}-${change}`} style={{ ["--i" as string]: index }}>
      <span className="ot-changes__icon">{done ? <Icon name="check" weight="bold"/> : <Icon name={changeIcon(change)}/>}</span>
      <span>{change}</span>
    </li>)}
  </ul>;
}

function Choices({ choices, onPick }: { choices: AnswerChoice[]; onPick: (command: string) => void }) {
  const dataset = useDataset();
  return <div className="ot-answer__choices">
    {choices.map((choice) => <button key={choice.command} type="button" className="ot-chip" onClick={() => onPick(choice.command)}>
      {choice.projectId && <span className="ot-swatch" style={{ ["--pc" as string]: colorOf(dataset, choice.projectId) }}/>}
      {choice.label}
      {choice.command.endsWith(" ") && <Icon name="arrow-right"/>}
    </button>)}
  </div>;
}

/** Hour marks for an axis: at most about eight, on a round step. */
function hourMarks(from: number, to: number): number[] {
  const hours = (to - from) / HOUR;
  const step = [1, 2, 3, 4, 6].find((candidate) => hours / candidate <= 8) ?? 6;
  const marks: number[] = [];
  for (let ts = from; ts <= to; ts += step * HOUR) marks.push(ts);
  return marks;
}

/** The day as one Strata layer: every entry in its Project's color, the running one in Ember. */
function Day({ view }: { view: DayView }) {
  const dataset = useDataset();
  const [hover, setHover] = useState<string | null>(null);
  const ends = view.items.map((item) => item.end ?? view.now);
  const from = Math.max(view.from, Math.floor(Math.min(view.items[0]!.start, view.from + 8 * HOUR) / HOUR) * HOUR);
  const to = Math.min(view.to, Math.ceil(Math.max(...ends, view.from + 18 * HOUR) / HOUR) * HOUR);
  const at = (ts: number) => `${(Math.min(to, Math.max(from, ts)) - from) / (to - from) * 100}%`;
  const projects = new Set(view.items.map((item) => item.projectId)).size;
  const live = view.items.some((item) => item.end === null);

  return <div className="ot-day">
    <div className="ot-answer__lede">
      <span className="ot-answer__label">Tracked today</span>
      <span className="ot-answer__figure">{formatDuration(view.total)}</span>
      <span className="ot-answer__meta">{view.items.length} {view.items.length === 1 ? "entry" : "entries"}, {projects} {projects === 1 ? "project" : "projects"}</span>
    </div>
    <div className="ot-ribbon" role="img" aria-label={`Today from ${formatClock(from)} to ${formatClock(to)}`}>
      <div className="ot-ribbon__track">
        {hourMarks(from, to).map((mark) => <i key={mark} className="ot-ribbon__tick" style={{ left: at(mark) }}/>)}
        {view.items.map((item, index) => <span
          key={item.id}
          className={`ot-ribbon__piece ${item.end === null ? "is-running" : ""} ${item.billable ? "" : "is-unbilled"} ${hover === item.id ? "is-hover" : ""} ${hover && hover !== item.id ? "is-dim" : ""}`}
          style={{ left: at(item.start), width: `calc(${at(item.end ?? view.now)} - ${at(item.start)})`, ["--pc" as string]: colorOf(dataset, item.projectId), ["--i" as string]: index }}
        />)}
        {view.now >= from && view.now <= to && <i className="ot-ribbon__now" style={{ left: at(view.now) }}/>}
      </div>
      <div className="ot-ribbon__hours" aria-hidden="true">
        {hourMarks(from, to).map((mark) => <span key={mark} className="ot-mono" style={{ left: at(mark) }}>{formatClock(mark).slice(0, 2)}</span>)}
      </div>
    </div>
    <ol className="ot-day__list" onMouseLeave={() => setHover(null)}>
      {view.items.map((item) => <li key={item.id} className={item.end === null ? "is-running" : ""} onMouseEnter={() => setHover(item.id)} style={{ ["--pc" as string]: colorOf(dataset, item.projectId) }}>
        <span className="ot-swatch"/>
        <span className="ot-day__what">
          <b>{item.project}</b>
          <span>{[item.client, item.description].filter(Boolean).join(", ")}</span>
        </span>
        <span className="ot-day__when ot-mono">{formatClock(item.start)}<i>to</i>{item.end === null ? "now" : formatClock(item.end)}</span>
        <span className="ot-day__took ot-mono">{formatDuration(item.duration)}</span>
      </li>)}
    </ol>
    {live && <span className="ot-sr-only">A timer is still running.</span>}
  </div>;
}

const PERIODS: Array<{ value: EarningsPeriod; label: string; command: string }> = [
  { value: "today", label: "Today", command: "how much did I earn today?" },
  { value: "week", label: "Week", command: "how much did I earn this week?" },
  { value: "month", label: "Month", command: "how much did I earn this month?" }
];

/** What a period earned: the figure first, then which Projects it came from. */
function Earnings({ view, rows, title, onPick }: { view: EarningsView; rows: AnswerRow[]; title: string; onPick: (command: string) => void }) {
  const dataset = useDataset();
  const [first, ...others] = rows;
  const totals = new Map<string, { amount: number; tracked: number }>();
  for (const share of view.shares) {
    const total = totals.get(share.currency) ?? { amount: 0, tracked: 0 };
    totals.set(share.currency, { amount: total.amount + share.amount, tracked: total.tracked + share.tracked });
  }
  // Currencies keep the order of the totals above; each is shared out on its own.
  const order = (currency: string) => { const index = rows.findIndex((row) => row.label === currency); return index < 0 ? rows.length : index; };
  const lead = first ? totals.get(first.label) : undefined;
  const hourly = lead && lead.tracked >= HOUR ? formatMoney({ amount: Math.round(lead.amount / (lead.tracked / HOUR)), currency: first!.label }) : null;
  const span = formatPeriod(view.from, Math.min(view.to - 1, Date.now()));

  return <div className="ot-earn">
    <div className="ot-earn__top">
      <div className="ot-answer__lede">
        <span className="ot-answer__label">{title}</span>
        {first
          ? <span className="ot-answer__figure">{first.value}</span>
          : <span className="ot-answer__figure is-empty">Nothing billable yet</span>}
        <span className="ot-answer__meta">
          {view.period === "today" ? "" : `${span}, `}{formatDuration(view.tracked)} tracked{hourly ? `, ${hourly} an hour` : ""}
        </span>
      </div>
      <Segmented size="sm" aria-label="Period" value={view.period} options={PERIODS} onChange={(period) => onPick(PERIODS.find((item) => item.value === period)!.command)}/>
    </div>
    {others.length > 0 && <div className="ot-earn__also">
      {others.map((row) => <span key={row.label}><b>{row.value}</b>{row.label}</span>)}
    </div>}
    {[...totals.keys()].sort((a, b) => order(a) - order(b)).map((currency) => <div key={currency} className="ot-earn__group">
      {totals.size > 1 && <h4 className="ot-earn__currency">{currency}</h4>}
      <ul className="ot-earn__shares" aria-label={`By project, ${currency}`}>
        {view.shares.filter((share) => share.currency === currency).map((share, index) => <li key={share.projectId} style={{ ["--pc" as string]: colorOf(dataset, share.projectId), ["--share" as string]: share.amount / totals.get(currency)!.amount, ["--i" as string]: index }}>
          <span className="ot-swatch"/>
          <span className="ot-earn__who"><b>{share.project}</b>{share.client && <span>{share.client}</span>}</span>
          <span className="ot-earn__hours ot-mono">{formatDuration(share.tracked)}</span>
          <span className="ot-earn__amount ot-mono">{formatMoney({ amount: share.amount, currency: share.currency })}</span>
          <i className="ot-earn__bar" aria-hidden="true"/>
        </li>)}
      </ul>
    </div>)}
  </div>;
}

/** A short message from ontrack: a refusal, a question back, or a note. */
function Notice({ answer }: { answer: Answer }) {
  const problem = answer.tone === "problem";
  return <div className={`ot-notice ${problem ? "is-problem" : ""}`}>
    <span className="ot-notice__icon"><Icon name={problem ? "warning" : "info"}/></span>
    <div>
      <h3>{answer.title}</h3>
      {answer.text && <p>{answer.text}</p>}
    </div>
  </div>;
}

const STEPS: Record<AssistantStep, { label: string; icon: IconName }> = {
  find_entries: { label: "Looking through your entries", icon: "clock" },
  summarize: { label: "Adding up your time", icon: "clock" },
  log_time: { label: "Logging the time", icon: "clock" },
  start_timer: { label: "Starting a timer", icon: "play" },
  stop_timer: { label: "Stopping the timer", icon: "stop" },
  update_entries: { label: "Updating entries", icon: "pencil" },
  delete_entries: { label: "Moving entries to Trash", icon: "trash" },
  list_invoices: { label: "Reading your invoices", icon: "receipt" },
  uninvoiced_work: { label: "Checking unbilled work", icon: "hand-coins" },
  draft_invoice: { label: "Drafting the invoice", icon: "receipt" },
  split_invoice: { label: "Splitting the invoice into installments", icon: "pie-chart" },
  mark_invoice_sent: { label: "Marking the invoice sent", icon: "send" },
  mark_invoice_paid: { label: "Marking the invoice paid", icon: "hand-coins" },
  find_events: { label: "Checking your calendar", icon: "calendar" },
  list_tasks: { label: "Checking your tasks", icon: "check-circle" },
  plan_event: { label: "Putting it on the calendar", icon: "calendar-plus" },
  compose: { label: "Putting it together", icon: "chat" }
};

/** What the assistant is doing, step by step, as it calls its tools; the last step is live. */
function Thinking({ steps }: { steps: AssistantStep[] }) {
  // Between tool rounds the model may still call more tools, so "compose" only shows while it is the latest step.
  const shown = steps.map((step, index) => ({ step, index })).filter(({ step, index }) => step !== "compose" || index === steps.length - 1);
  const trail = [{ key: "read", label: "Reading your question", icon: "chat" as IconName }, ...shown.map(({ step, index }) => ({ key: `${index}-${step}`, ...STEPS[step] }))];
  return <div className="ot-thinking" role="status">
    <ol className="ot-thinking__trail">
      {trail.map((item, index) => {
        const live = index === trail.length - 1;
        return <li key={item.key} className={live ? "is-live" : "is-done"}>
          <span className="ot-thinking__icon">{live ? <Icon name={item.icon}/> : <Icon name="check" weight="bold"/>}</span>
          <span className="ot-thinking__label">{item.label}</span>
        </li>;
      })}
    </ol>
    <div className="ot-thinking__bars" aria-hidden="true"><i/><i/><i/></div>
  </div>;
}

/** Where the question was typed, so it can travel from the bar into the answer's header. */
export interface Flight { id: number; x: number; y: number; size: number }

const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * The question, echoed above its answer. On a new question a copy of the text flies from the
 * input to this spot; the copy lives on the page so the panel's clipping does not cut it off.
 */
function Asked({ text, flight }: { text: string; flight: Flight | null }) {
  const ref = useRef<HTMLParagraphElement>(null);
  useLayoutEffect(() => {
    const target = ref.current;
    if (!target || !flight || typeof target.animate !== "function" || reducedMotion()) return;
    const rect = target.getBoundingClientRect();
    const style = getComputedStyle(target);
    const size = parseFloat(style.fontSize) || 13;
    const copy = document.createElement("span");
    copy.className = "ot-answer__flight";
    copy.textContent = text;
    Object.assign(copy.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`, font: style.font, lineHeight: `${rect.height}px` });
    document.body.appendChild(copy);
    const ink = getComputedStyle(document.documentElement).getPropertyValue("--ot-ink").trim() || style.color;
    const timing = { duration: 620, easing: "cubic-bezier(.16, 1, .3, 1)" };
    copy.animate([
      { transform: `translate(${flight.x - rect.left}px, ${flight.y - rect.top - rect.height / 2}px) scale(${flight.size / size})`, color: ink, opacity: 1 },
      { opacity: 1, offset: 0.82 },
      { transform: "none", color: style.color, opacity: 0 }
    ], timing).finished.catch(() => undefined).finally(() => copy.remove());
    target.animate([{ opacity: 0 }, { opacity: 0, offset: 0.75 }, { opacity: 1 }], timing);
    return () => copy.remove();
    // Only a new question flies; re-renders of the same one stay put.
  }, [flight?.id]);
  return <p ref={ref} className="ot-answer__asked" title={text}>{text}</p>;
}

const ease = [0.16, 1, 0.3, 1] as const;

export function AnswerCard({ asked, flight, phase, thinking, steps, preview, answer, onClose, onPick, onCancel, onApply }: {
  asked: string;
  flight: Flight | null;
  /** Changes whenever the content is replaced, so the old one can leave before the new one arrives. */
  phase: string;
  thinking: boolean;
  steps: AssistantStep[];
  preview: { changes: string[] } | null;
  answer: Answer | null;
  onClose: () => void;
  onPick: (command: string) => void;
  onCancel: () => void;
  onApply: () => void;
}) {
  const view = answer?.view;
  let body: ReactNode = null;
  if (thinking) body = <Thinking steps={steps}/>;
  else if (preview) body = null;
  else if (answer && view?.kind === "day") body = <Day view={view}/>;
  else if (answer && view?.kind === "earnings") body = <Earnings view={view} rows={answer.rows ?? []} title={answer.title} onPick={onPick}/>;
  else if (answer?.reply || answer?.sources?.length) body = <>
    {answer.text && <Reply text={answer.text}/>}
    {answer.done?.length ? <Changes changes={answer.done} done/> : null}
    {answer.sources?.length ? <SourceCards sources={answer.sources} onPick={onPick}/> : null}
  </>;
  else if (answer) body = <Notice answer={answer}/>;

  return <>
    <header className="ot-answer__head">
      <OTrackMark size={16} running={thinking}/>
      <Asked text={asked} flight={flight}/>
      <button type="button" className="ot-answer__close" onClick={onClose} aria-label="Dismiss" disabled={thinking}><Icon name="close"/></button>
    </header>
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={phase}
        className="ot-answer__body"
        initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.36, ease } }}
        exit={{ opacity: 0, y: -4, filter: "blur(2px)", transition: { duration: 0.14 } }}
      >
        {body}
        {preview && <div className="ot-preview">
          {answer?.text && <Reply text={answer.text}/>}
          <h3 className="ot-preview__title">{preview.changes.length === 1 ? "Review this change" : `Review ${preview.changes.length} changes`}</h3>
          <Changes changes={preview.changes}/>
          <div className="ot-preview__actions">
            <Button tone="ghost" onClick={onCancel}>Cancel</Button>
            <Button tone="accent" icon="check" onClick={onApply}>Apply changes</Button>
          </div>
        </div>}
        {answer?.choices && !preview && !thinking && <Choices choices={answer.choices} onPick={onPick}/>}
      </motion.div>
    </AnimatePresence>
  </>;
}
